import { switchMap, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserProfile } from '@content-platform/web-angular-administration-shared-data-access-graph-api';
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';
import { uniq } from 'lodash';
import { Observable, of, zip } from 'rxjs';
import { Role } from '../models';
import { ApiParams } from './api-params';
import { Resource } from './resource';
import { MsGraphApiService } from '@content-platform/web-angular-administration-shared-data-access-ms-graph-api';
import * as _ from 'lodash';
import { WebAngularSharedDataAccessFeatureFlagApisService } from '@content-platform/web-angular-shared-data-access-feature-flag-api';

@Injectable()
export class RoleResource extends Resource<Role> {
  constructor(
    http: HttpClient,
    private msGraphApiService: MsGraphApiService,
    logger: LoggerService,
    private apiParams: ApiParams,
    featureFlagSvc: WebAngularSharedDataAccessFeatureFlagApisService
  ) {
    super(http, logger.instance('RoleResource'), featureFlagSvc);
  }

  getUrl(flag): string {
    return this.apiParams.getUrl('roles', undefined, flag);
  }

  /**
   * Makes sure the users list is a list of strings.
   *
   * @private
   * @param role
   * @returns string of user ids
   */
  private getUserIds(role: Role): string[] {
    // We are getting some fake users from backend in the users list for certain roles (v4 uuid check)
    role.users = [
      ...(<any[]>role.users)
        .filter((user) => !!user)
        .filter(
          (user) =>
            (user as string).search(
              /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i
            ) !== -1
        ),
    ];
    return (<any[]>role.users)
      .filter((user) => !!user)
      .map((user) => {
        if (typeof user === 'string') {
          return user;
        }
        return (<UserProfile>user).objectId;
      });
  }

  /**
   * Getting user profiles from the users in the roles
   *
   * @private
   * @param {Role} role
   * @returns {Observable<UserProfile[]>}
   * @memberof RoleResource
   */
  private getUsersProfilesFromRole(role: Role): Observable<UserProfile[]> {
    const users: string[] = this.getUserIds(role);

    return users.length > 0 ? this.msGraphApiService.getUsers(users) : of([]);
  }

  /**
   * to get all roles from the database and mapiing user profiles against the users
   *
   * @returns {Observable<Role[]>}
   * @memberof RoleResource
   */
  query(): Observable<Role[]> {
    return super.query().pipe(
      switchMap((roles) => {
        if (roles.length > 0) {
          return this.mapUserProfilesToRoles(roles);
        }
        return of(roles);
      })
    );
  }

  /**
   * to get all roles from the database and mapiing user profiles against the users
   *
   * @returns {Observable<Role[]>}
   * @memberof RoleResource
   */
  queryById(roleIds: number[]): Observable<Role[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queryByIdObs: Observable<any>[] = [];
    roleIds.forEach((id: number) => {
      queryByIdObs.push(
        super.get(id)
      );
    });

    return zip(...queryByIdObs).pipe(
      switchMap((roles) => {
        if (roles.length > 0) {
          const rolesList = _.flatten(roles);
          return this.mapUserProfilesToRoles(rolesList);
        }
        return of(roles);
      })
    );
  }

  /**
   * getting the single role for an id and mapping the user profile for the user in role.
   *
   * @param {(number | string)} id
   * @returns {Observable<Role>}
   * @memberof RoleResource
   */
  get(id: number | string): Observable<Role> {
    return super
      .get(id)
      .pipe(
        switchMap((role) =>
          this.getUsersProfilesFromRole(role).pipe(map((results) => ({ ...role, users: results })))
        )
      );
  }

  update(role: Role): Observable<Role> {
    return super
      .update(role)
      .pipe(
        switchMap((updatedRole) =>
          this.getUsersProfilesFromRole(updatedRole).pipe(
            map((results) => ({ ...updatedRole, users: results }))
          )
        )
      );
  }

  create(role: Role): Observable<Role> {
    return super
      .create(role)
      .pipe(
        switchMap((newRole) =>
          this.getUsersProfilesFromRole(newRole).pipe(
            map((results) => ({ ...newRole, users: results }))
          )
        )
      );
  }

  /**
   * Getting user profiles of all the roles, in one request, returns the user entities, as an
   * object. The key is the user id.
   *
   * @private
   * @param {Role} role
   * @returns {Observable<{[id: string]: UserProfile}>}
   * @memberof RoleResource
   */
  private getUserProfilesFromRoles(roles: Role[]): Observable<{ [id: string]: UserProfile }> {
    const users: string[] = [].concat(...roles.map((role) => this.getUserIds(role)));

    return (users.length > 0 ? this.msGraphApiService.getUsers(uniq(users)) : of([])).pipe(
      map((userProfiles) =>
        userProfiles.reduce((entities: { [id: string]: UserProfile }, user: UserProfile) => {
          if (user && user.objectId) {
            return {
              ...entities,
              [user.objectId]: user,
            };
          }
          return entities;
        }, {})
      )
    );
  }

  /**
   * Mapping users with their profiles in the roles
   *
   * @private
   * @param {Role[]} roles
   * @returns {Observable<Role[]>}
   * @memberof RoleResource
   */
  private mapUserProfilesToRoles(roles: Role[]): Observable<Role[]> {
    return this.getUserProfilesFromRoles(roles).pipe(
      map((userEntities) =>
        roles.map((role) => {
          const users: UserProfile[] = this.getUserIds(role)
            .map((userId) => userEntities[userId])
            .filter((user) => !!user);

          return { ...role, users };
        })
      )
    );
  }

  /**
   * for logging purpose returns the class name only
   *
   * @readonly
   * @type {string}
   * @memberof RoleResource
   */
  get entityName(): string {
    return 'Role';
  }

  /**
   * request to convert userprofiles to list of user ids
   *
   * @protected
   * @param {Role} role
   * @returns {Role}
   * @memberof RoleResource
   */
  protected transformRequest(role: Role): Role {
    return {
      ...role,
      users: (<any[]>role.users).map((user) => {
        if (typeof user === 'string') {
          return user;
        }
        return (<UserProfile>user).objectId;
      }),
    };
  }
}
